import PhoneInputBase, {
  isValidPhoneNumber,
  parsePhoneNumber,
  type Country,
} from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { cn } from '@/lib/utils'

/**
 * The phone field. Phone numbers are the account identity, so this is the one place the
 * web app turns what somebody types into the E.164 the API expects.
 *
 * ## Why the country travels separately
 *
 * The API takes `{ phoneCountry, phone }`, not a single string, and validates the pair with
 * libphonenumber server-side. `0712345678` is a valid number in several countries and a
 * different person in each, so the country is not something the server can infer — and this
 * component is what makes the operator pick one rather than guess.
 *
 * ## Validation happens HERE first
 *
 * `react-phone-number-input` bundles libphonenumber-js, the same numbering-plan data the
 * backend's libphonenumber uses. So an invalid number is caught before the request is made
 * rather than after a round-trip, which is the whole reason for the dependency. The server
 * still re-validates — a browser check is a courtesy, never a control.
 *
 * The import is deliberately from the package root rather than `/min`: the min metadata drops
 * the per-type validation this needs to tell a mobile number from a landline, and the whole
 * point here is that the number can receive an SMS.
 */
export function PhoneInput({
  value, onChange, country, onCountryChange, disabled, autoFocus, id, className,
}: {
  /** E.164, or '' when empty/incomplete. Never a national-format string. */
  value: string
  onChange: (e164: string) => void
  country: Country
  onCountryChange: (c: Country) => void
  disabled?: boolean
  autoFocus?: boolean
  id?: string
  className?: string
}) {
  return (
    <PhoneInputBase
      id={id}
      international
      defaultCountry={country}
      country={country}
      onCountryChange={(c) => c && onCountryChange(c)}
      value={value || undefined}
      // The library hands back `undefined` for an empty field; '' keeps callers on one type.
      onChange={(v) => onChange(v ?? '')}
      disabled={disabled}
      autoFocus={autoFocus}
      className={cn('axis-phone-input', className)}
      numberInputProps={{ className: 'erp-input w-full' }}
    />
  )
}

/** True when `e164` is a number that can actually exist. Empty is NOT valid. */
export function phoneIsValid(e164: string): boolean {
  return !!e164 && isValidPhoneNumber(e164)
}

/**
 * The alpha-2 a number belongs to, for sending alongside it.
 *
 * Falls back to the picker's current country when the number is too incomplete to resolve —
 * the server re-derives it from the number anyway, so a stale fallback is corrected rather
 * than stored.
 */
export function countryOf(e164: string, fallback: Country): Country {
  if (!e164) return fallback
  return parsePhoneNumber(e164)?.country ?? fallback
}

/** `+254712345678` → `+254 712 345678`, for read-only display. Never for form state. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '—'
  return parsePhoneNumber(e164)?.formatInternational() ?? e164
}

/** The deployment's home country — every form starts here rather than on an empty picker. */
export const DEFAULT_COUNTRY: Country = 'KE'
